'use client';

import React, { JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { CheckoutStepInputComponentProps } from '@/types/checkout/CheckoutStepInputComponent';

import { checkoutFetchShippingMethods } from '@/actions/checkout';
import { storeIntegrationStampsAddressVerification } from '@/actions/store';
import { usersUpdateAddress } from '@/actions/user';

import { CHECKOUT_STEP_BUYLIST_LOCATION } from '@/constants/checkout';
import {
	TX_CHECKOUT_ADDRESS_VERIFICATION,
	TX_CHECKOUT_ADDRESS_VERIFICATION_ADDRESS_ENTERED,
	TX_CHECKOUT_ADDRESS_VERIFICATION_DOES_NOT_MATCH,
	TX_CHECKOUT_BUYLIST_NO_LOCATIONS,
	TX_CHECKOUT_PICKUP_DISABLED_NOTICE,
	TX_LOCATIONS,
	TX_PICKUP,
	TX_SAVE,
	TX_SHIPPING_METHOD,
	TX_SHIPPING_METHODS,
	TX_STORE_LOCATIONS,
} from '@/constants/strings';

import useCart from '@/hooks/useCart';
import useCheckout from '@/hooks/useCheckout';
import usePrevious from '@/hooks/usePrevious';
import useStore from '@/hooks/useStore';

import StoreLocation from '@/models/locations/StoreLocation';

import { addShippingInfoEvent } from '@/utils/analytics';

import LoadingIcon from '@/components/Icons/LoadingIcon';
import Radio from '@/components/Input/Radio';
import ShippingMethodLocationInput from '@/components/Checkout/blocks/steps/shipping-method/ShippingMethodLocationInput';

import stepStyles from '../_styles/CheckoutStepComponent.module.scss';
import styles from './_styles/CheckoutStepBuylistLocationInput.module.scss';



// Prop types
// Imported from src/types/checkout/...

export const CheckoutStepBuylistLocationInput = (props: CheckoutStepInputComponentProps): JSX.Element => {
  
	// Dispatch
	// const dispatch = useDispatch<AppDispatch>();

	// Constants
	// None yet

	// Props
	const {
		config,
		setStepData,
	} = props;

	// Local state
	const [ selectedLocation, setSelectedLocation ] = useState<StoreLocation | null>(null);

	// Checkout session
	const { checkoutSession } = useCheckout();
	
	// Store
	const { store } = useStore();

	// Cart
	const { activeBuylistCart } = useCart();

	// Internationalization
	const { t } = useTranslation();

	// Derived constants
	// Locations where a product line in the cart has buylist disabled — hide
	// these so the customer can't pick a location that won't buy their items.
	const excludedLocationUuids = useMemo(() => {
		const set = new Set<string>();
		for(const item of activeBuylistCart?.items ?? []) {
			const exclusions = item.product?.productLine?.buylistPricing?.excludedLocations ?? [];
			for(const exclusion of exclusions) {
				if(exclusion.publicUuid) { set.add(exclusion.publicUuid); }
			}
		}
		return set;
	}, [activeBuylistCart]);

	const availableLocations = useMemo(() => {
		return (store?.locations ?? []).filter((loc) => !(loc.publicUuid && excludedLocationUuids.has(loc.publicUuid)));
	}, [store, excludedLocationUuids]);

	// Methods
	const isOptionSelected = (opt: StoreLocation): boolean => {
		return !!(selectedLocation?.publicUuid && selectedLocation.publicUuid === opt.publicUuid);
	}

	const setLocation = (loc: StoreLocation) => {
		
		if(!loc?.publicUuid) {
			setSelectedLocation(null);
		}

		setSelectedLocation(loc);
		setStepData(config, {
			location: loc, 
		});
	}
	
	// Effects
	// None yet

	// Render
	return (
		<div className={`${styles.CheckoutStepBuylistLocationInput} ${stepStyles.CheckoutStepComponent}`}>
			<div className={styles.locationsWrapper}>
				<div className={stepStyles.checkoutFormSubheader}>
					{t(TX_STORE_LOCATIONS)}
				</div>
				<div className={stepStyles.checkoutStepRadioForm}>
					{!store ?
						<div className={stepStyles.checkoutRadioLoadingWrapper}>
							<LoadingIcon />
						</div> :
						availableLocations.length === 0 ? <div className={styles.noLocationsNotice}>{t(TX_CHECKOUT_BUYLIST_NO_LOCATIONS)}</div> : <div className={stepStyles.radioOptionsWrapper}>
													
							{availableLocations.map((loc, i) => {
								return <div key={i} className={`${stepStyles.radioOptionElementWrapper} ${isOptionSelected(loc) ? stepStyles.selected : ''}`}>
									<div className={stepStyles.radioInputWrapper}>
										<Radio
											id={`storeLocation-${i}`}
											className={stepStyles.locInput}
											name='store-location'
											adminTheme={false}
											disabled={false}
											value={loc.publicUuid}
											checked={isOptionSelected(loc)}
											onChange={() => setLocation(loc)} />
									</div>
									<label htmlFor={`storeLocation-${i}`}>
										<div className={stepStyles.radioLabelWrapper}>
											<div className={stepStyles.radioTitle}>{loc.name}</div>
											{loc.address && (
												<div className={stepStyles.radioSubtitle}>
													{loc.address.formatDefault({ hideName: true, includePhone: true }).map((line, lx) => (
														<div key={lx}>{line}</div>
													))}
												</div>
											)}
										</div>
									</label>
								</div>;
							})}

						</div>
					}
				</div>
			</div>
		</div>
	);
};

export default CheckoutStepBuylistLocationInput;




